import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Helmet } from "react-helmet";
import { toast } from 'react-toastify';

import history from '../../../history';
import Footer from "../../Footer/FooterBottom";
import Field from "../../Admin/Question/View/Background/Field";
import ClassTypeData from "../../Config/classType.json";

import SearchSidebar from "./SearchSidebar";
import SearchResults from "./SearchResults";
import ShowAllModal from "./ShowAllModal";

import { searchLessons as fetchSearch } from '../actions/';
import { fetchIndices } from '../../Admin/Indices/actions/';
import { getIndeices } from "../../../context/sessionStorage";
import { fetchClassRooms } from '../../School/ClassRooms/actions';
import { fetchStudentInClassRooms } from '../../School/StudentInClassRooms/actions/';

const Search = () => {
    const { t } = useTranslation('common');
    const dispatch = useDispatch();
    const { id } = useParams();

    const searchInputRef = useRef(null);
    const searchStartTimeRef = useRef(null);

    const user = useSelector(state => state.user);
    const search = useSelector(state => state.search);
    const classRooms = useSelector(state => state.classRooms);
    const indicesState = useSelector(state => state.indices);

    const [classTypeOptions] = useState(ClassTypeData.ClassType || []);
    const [classTypeId, setClassTypeId] = useState("");
    const [showResult, setShowResult] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [activeTab, setActiveTab] = useState("all");
    const [searchTime, setSearchTime] = useState(null);

    // Advanced search options (count, isPublic, hasSound, sortBy)
    const [count, setCount] = useState(10);
    const [isPublic, setIsPublic] = useState(false);
    const [hasSound, setHasSound] = useState(false);
    const [sortBy, setSortBy] = useState("newest");

    const [filters, setFilters] = useState({
        IsExams: true,
        IsFun: true,
        IsBooks: true,
        IsQuizzes: true,
        IsLessons: true,
        IsFiles: true,
        IsSkills: true,
        IsSelfDevelopment: false
    });

    const [modalState, setModalState] = useState({
        showAll: false,
        type: "",
        title: "",
        background: "",
        count: 0,
        renderFn: null
    });

    useEffect(() => {
        if (
            user &&
            user.user &&
            user.user.UserType &&
            (user.user.UserType.Id === 3 || user.user.UserType.Id === 4) &&
            classRooms && classRooms.length === 0
        ) {
            dispatch(fetchClassRooms());
        }
    }, [user, classRooms, dispatch]);

    // Search execution only triggers explicitly when user clicks Search button or presses Enter
    const executeSearch = useCallback((keyToSearch) => {
        const query = keyToSearch !== undefined ? keyToSearch : searchValue;
        if (query) {
            if (query.trim().length > 2) {
                const trimmedKey = query.trim();
                searchStartTimeRef.current = performance.now();
                setShowResult(true);

                // Update URL to /Search/:key if different, otherwise dispatch directly
                if (id !== trimmedKey) {
                    history.push(`/Search/${encodeURIComponent(trimmedKey)}`);
                } else {
                    const postData = {
                        key: trimmedKey,
                        IsExams: filters.IsExams,
                        IsFiles: filters.IsFiles,
                        IsBooks: filters.IsBooks,
                        IsQuizzes: filters.IsQuizzes,
                        IsSelfDevelopment: filters.IsSelfDevelopment,
                        IsLessons: filters.IsLessons,
                        IsFun: filters.IsFun,
                        IsSkills: filters.IsSkills,
                        ClassTypeId: classTypeId,
                        count: count,
                        IsPublic: isPublic,
                        HasSound: hasSound,
                        SortBy: sortBy
                    };
                    dispatch(fetchSearch(postData));
                }
            } else {
                let _toastId = toast(t('Processing'), {
                    position: "bottom-left",
                    autoClose: false
                });

                toast.update(_toastId, {
                    render: () => <div> <i className="fa-spin fas fa-spinner"></i> {t('Processing')} </div>,
                    autoClose: false,
                    closeOnClick: false,
                });

                toast.update(_toastId, {
                    render: () => <>
                        <div className="h5">
                            <i className="icon fal fa-magnifying-glass-minus"></i>
                            {t('Pages.Search.toast.KeyLength.Title', 'طول مفتاح البحث غير كافٍ')}
                        </div>
                        <div className="h6">{t('Pages.Search.toast.KeyLength.Body', 'يرجى إدخال 3 أحرف على الأقل في مفتاح البحث.')}</div>
                    </>,
                    type: toast.TYPE.ERROR,
                    position: "bottom-left",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: false,
                    pauseOnHover: true,
                    draggable: true,
                });
            }
        } else {
            let _toastId = toast(t('Processing'), {
                position: "bottom-left",
                autoClose: false
            });

            toast.update(_toastId, {
                render: () => <div> <i className="fa-spin fas fa-spinner"></i> {t('Processing')} </div>,
                autoClose: false,
                closeOnClick: false,
            });

            toast.update(_toastId, {
                render: () => <>
                    <div className="h5">
                        <i className="icon fal fa-magnifying-glass-minus"></i>
                        {t('Pages.Search.toast.NoKey.Title', 'مفتاح البحث فارغ')}
                    </div>
                    <div className="h6">{t('Pages.Search.toast.NoKey.Body', 'يرجى إدخال كلمة أو نص للبحث.')}</div>
                </>,
                type: toast.TYPE.ERROR,
                position: "bottom-left",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
            });
        }
    }, [searchValue, filters, classTypeId, count, isPublic, hasSound, sortBy, id, dispatch, t]);

    // Track total search duration in seconds
    useEffect(() => {
        if (search.isLoaded && searchStartTimeRef.current) {
            const duration = ((performance.now() - searchStartTimeRef.current) / 1000).toFixed(2);
            setSearchTime(duration);
            searchStartTimeRef.current = null;
        }
    }, [search.isLoaded]);

    // Initial loading effect dependent ONLY on route param `id` and `dispatch`
    useEffect(() => {
        if (id) {
            const decodedId = decodeURIComponent(id).trim();
            setSearchValue(decodedId);
            searchStartTimeRef.current = performance.now();
            dispatch(fetchSearch({
                key: decodedId,
                IsExams: filters.IsExams,
                IsFiles: filters.IsFiles,
                IsBooks: filters.IsBooks,
                IsQuizzes: filters.IsQuizzes,
                IsSelfDevelopment: filters.IsSelfDevelopment,
                IsLessons: filters.IsLessons,
                IsFun: filters.IsFun,
                IsSkills: filters.IsSkills,
                ClassTypeId: classTypeId,
                count: count,
                IsPublic: isPublic,
                HasSound: hasSound,
                SortBy: sortBy
            }));
            setShowResult(true);
        }

        let storedIndices = getIndeices();
        if (storedIndices === null || storedIndices.length === 0) {
            dispatch(fetchIndices());
        }

        if (window.scrollToPosition) {
            window.scrollToPosition(0, 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, dispatch]);

    const openShowAllModal = (type, title, background, listCount, renderFn) => {
        setModalState({
            showAll: true,
            type,
            title,
            background,
            count: listCount,
            renderFn
        });
    };

    const closeShowAllModal = () => {
        setModalState(prev => ({ ...prev, showAll: false }));
    };

    // Calculate category counts for live badges directly from Results
    const categoryCounts = {
        Lessons: search.Results ? search.Results.filter(x => (x.Type === 'Lessons' || x.Category === 'Lessons')).length : 0,
        Files: search.Results ? search.Results.filter(x => (x.Type === 'Files' || x.Category === 'Files')).length : 0,
        Exams: search.Results ? search.Results.filter(x => (x.Type === 'Exams' || x.Category === 'Exams')).length : 0,
        Quizzes: search.Results ? search.Results.filter(x => (x.Type === 'Quizzes' || x.Category === 'Quizzes')).length : 0,
        Games: search.Results ? search.Results.filter(x => (x.Type === 'Games' || x.Category === 'Games' || x.Type === 'Fun' || x.Category === 'Fun')).length : 0,
        Books: search.Results ? search.Results.filter(x => (x.Type === 'Books' || x.Category === 'Books')).length : 0
    };

    return (
        <React.Fragment>
            <Helmet>
                <title>{`${t('Pages.Search.Title', 'البحث السريع المتقدم')} - ${t('WebsiteName')}`}</title>
            </Helmet>

            <div className="position-relative" style={{ minHeight: '85vh', overflow: 'hidden' }}>
                <Field hideHeader={true} absolute={true} />
                <div className="container-fluid px-3 px-md-4 py-3 position-relative" style={{ zIndex: 10 }}>
                    <SearchSidebar
                        searchInputRef={searchInputRef}
                        searchValue={searchValue}
                        setSearchValue={setSearchValue}
                        filters={filters}
                        setFilters={setFilters}
                        classTypeId={classTypeId}
                        setClassTypeId={setClassTypeId}
                        classTypeOptions={classTypeOptions}
                        count={count}
                        setCount={setCount}
                        isPublic={isPublic}
                        setIsPublic={setIsPublic}
                        hasSound={hasSound}
                        setHasSound={setHasSound}
                        sortBy={sortBy}
                        setSortBy={setSortBy}
                        onSearch={executeSearch}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        categoryCounts={categoryCounts}
                    />

                    <SearchResults
                        search={search}
                        showResult={showResult}
                        filters={filters}
                        indices={indicesState?.nodes || indicesState}
                        classRooms={classRooms}
                        user={user}
                        fetchStudentInClassRooms={fetchStudentInClassRooms}
                        onShowAll={openShowAllModal}
                        searchValue={searchValue}
                        onSelectSuggestedKey={(suggestedKey) => {
                            setSearchValue(suggestedKey);
                            executeSearch(suggestedKey);
                        }}
                        activeTab={activeTab}
                        searchTime={searchTime}
                    />
                </div>
            </div>

            <ShowAllModal
                modalState={modalState}
                onClose={closeShowAllModal}
                searchValue={searchValue}
                show={modalState.showAll}
                onHide={closeShowAllModal}
                title={modalState.title}
                background={modalState.background}
                count={modalState.count}
                renderFn={modalState.renderFn}
            />

            <Footer />
        </React.Fragment>
    );
};

export default Search;
